import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Home, TrendingUp, Users, Award, Target, FileDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import * as XLSX from 'xlsx';

interface AreaScore {
  area: string;
  score: number;
  count: number;
  globalAvg?: number;
  collegeAvg?: number;
}

interface RunComparison {
  run: string;
  avgScore: number;
  participants: number;
}

interface TrendData {
  date: string;
  avgScore: number;
  participants: number;
}

const AdminAnalytics = () => {
  const { user, isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState<any[]>([]);
  const [colleges, setColleges] = useState<any[]>([]);
  const [selectedRun, setSelectedRun] = useState<string>("all");
  const [selectedCollege, setSelectedCollege] = useState<string>("all");
  const [selectedRole, setSelectedRole] = useState<string>("all");
  const [areaScores, setAreaScores] = useState<AreaScore[]>([]);
  const [runComparisons, setRunComparisons] = useState<RunComparison[]>([]);
  const [trendData, setTrendData] = useState<TrendData[]>([]);
  const [stats, setStats] = useState({
    totalParticipants: 0,
    avgOverallScore: 0,
    completionRate: 0,
    topArea: "",
    weakestArea: "",
  });

  useEffect(() => {
    if (!isLoading && !isAdmin) {
      toast({
        title: "Access Denied",
        description: "You don't have admin privileges",
        variant: "destructive",
      });
      navigate("/");
    }
  }, [isAdmin, isLoading, navigate, toast]);

  useEffect(() => {
    if (isAdmin) {
      fetchRuns();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (runs.length > 0) {
      fetchAnalytics();
    }
  }, [selectedRun, selectedCollege, selectedRole, runs]);

  const fetchRuns = async () => {
    const { data } = await supabase
      .from("assessment_runs")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setRuns(data);

    const { data: collegesData } = await supabase
      .from("colleges")
      .select("*")
      .order("name");
    if (collegesData) setColleges(collegesData);

    setLoading(false);
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    // First fetch area scores so other functions can use the data
    const scores = await fetchAreaScores();
    // Then fetch the other analytics in parallel
    await Promise.all([
      fetchRunComparisons(),
      fetchTrendData(),
      fetchStats(scores),
    ]);
    setLoading(false);
  };

  const fetchAreaScores = async (): Promise<AreaScore[]> => {
    if (!user) return [];

    // Get user's college ID
    const { data: userProfile } = await supabase
      .from("profiles")
      .select("college_id")
      .eq("id", user.id)
      .single();

    const userCollegeId = userProfile?.college_id;

    // Get all attempts for the selected run (for global average - NO college/role filtering)
    let globalAttemptsQuery = supabase.from("attempts").select("id, user_id");
    if (selectedRun !== "all") {
      globalAttemptsQuery = globalAttemptsQuery.eq("run_id", selectedRun);
    }
    const { data: allAttempts } = await globalAttemptsQuery;

    if (!allAttempts || allAttempts.length === 0) {
      setAreaScores([]);
      return [];
    }

    // Get "My Score" - attempts by the logged-in user in the selected run only
    let myAttempts = allAttempts.filter(a => a.user_id === user.id);

    // Get attempts from user's college (for college average - always user's college, not selected college)
    let userCollegeAttempts = allAttempts;
    if (userCollegeId) {
      const { data: collegeProfiles } = await supabase
        .from("profiles")
        .select("id")
        .eq("college_id", userCollegeId);

      const collegeUserIds = new Set(collegeProfiles?.map(p => p.id));
      userCollegeAttempts = allAttempts.filter(a => collegeUserIds.has(a.user_id));
    }

    if (myAttempts.length === 0) {
      setAreaScores([]);
      return [];
    }

    // Fetch responses for all three groups in parallel
    // Global: ALL attempts in the run (no college/role filtering)
    const globalAttemptIds = allAttempts.map(a => a.id);
    // User's College: only attempts from user's college
    const userCollegeAttemptIds = userCollegeAttempts.map(a => a.id);
    // My Score: only attempts from logged-in user in the selected run
    const myAttemptIds = myAttempts.map(a => a.id);

    console.log("Debug - fetchAreaScores:", {
      userCollegeId,
      globalAttemptCount: globalAttemptIds.length,
      userCollegeAttemptCount: userCollegeAttemptIds.length,
      myAttemptCount: myAttemptIds.length,
    });

    const [{ data: globalResponses }, { data: userCollegeResponses }, { data: myResponses }] = await Promise.all([
      supabase.from("responses").select("competence_area, rating").in("attempt_id", globalAttemptIds),
      supabase.from("responses").select("competence_area, rating").in("attempt_id", userCollegeAttemptIds),
      supabase.from("responses").select("competence_area, rating").in("attempt_id", myAttemptIds),
    ]);

    console.log("Debug - response counts:", {
      globalResponseCount: globalResponses?.length || 0,
      userCollegeResponseCount: userCollegeResponses?.length || 0,
      myResponseCount: myResponses?.length || 0,
    });

    const resultScores: AreaScore[] = [];

    if (myResponses && myResponses.length > 0) {
      // Build maps for all three groups
      const myAreaMap = new Map<string, { total: number; count: number; minRating: number; maxRating: number }>();
      const collegeAreaMap = new Map<string, { total: number; count: number; minRating: number; maxRating: number }>();
      const globalAreaMap = new Map<string, { total: number; count: number; minRating: number; maxRating: number }>();

      // Process my responses (My Score - logged-in user only)
      myResponses.forEach((response) => {
        const current = myAreaMap.get(response.competence_area) || {
          total: 0,
          count: 0,
          minRating: 999,
          maxRating: 0,
        };
        myAreaMap.set(response.competence_area, {
          total: current.total + response.rating,
          count: current.count + 1,
          minRating: Math.min(current.minRating, response.rating),
          maxRating: Math.max(current.maxRating, response.rating),
        });
      });

      // Process college responses (College Avg - user's college)
      userCollegeResponses?.forEach((response) => {
        const current = collegeAreaMap.get(response.competence_area) || {
          total: 0,
          count: 0,
          minRating: 999,
          maxRating: 0,
        };
        collegeAreaMap.set(response.competence_area, {
          total: current.total + response.rating,
          count: current.count + 1,
          minRating: Math.min(current.minRating, response.rating),
          maxRating: Math.max(current.maxRating, response.rating),
        });
      });

      // Process global responses (Global Avg - all participants)
      globalResponses?.forEach((response) => {
        const current = globalAreaMap.get(response.competence_area) || {
          total: 0,
          count: 0,
          minRating: 999,
          maxRating: 0,
        };
        globalAreaMap.set(response.competence_area, {
          total: current.total + response.rating,
          count: current.count + 1,
          minRating: Math.min(current.minRating, response.rating),
          maxRating: Math.max(current.maxRating, response.rating),
        });
      });

      // Calculate normalized scores for each area using a consistent global rating scale
      Array.from(myAreaMap.entries()).forEach(([area, { total, count, minRating, maxRating }]) => {
        // Get global data for this area to use as the normalization scale
        const globalData = globalAreaMap.get(area);
        if (!globalData) return;

        // Use global min/max as the standard for all normalizations
        const globalRatingRange = globalData.maxRating - globalData.minRating;
        if (globalRatingRange === 0) return;

        // Normalize My Score using global scale
        const myAvgRating = total / count;
        const myScore = ((myAvgRating - globalData.minRating) / globalRatingRange) * 100;

        // Normalize College Average using global scale
        const collegeData = collegeAreaMap.get(area);
        let collegeScore = 0;
        if (collegeData) {
          const collegeAvgRating = collegeData.total / collegeData.count;
          collegeScore = ((collegeAvgRating - globalData.minRating) / globalRatingRange) * 100;
        }

        // Normalize Global Average using global scale
        const globalAvgRating = globalData.total / globalData.count;
        const globalScore = ((globalAvgRating - globalData.minRating) / globalRatingRange) * 100;

        // Properly handle area names
        const areaParts = area.split(" ");
        const shortName = areaParts.length > 1 
          ? areaParts[0] + " " + areaParts[1]
          : areaParts[0];

        resultScores.push({
          area: shortName,
          score: Math.max(0, Math.min(100, Math.round(myScore))),
          count,
          globalAvg: Math.max(0, Math.min(100, Math.round(globalScore))),
          collegeAvg: Math.max(0, Math.min(100, Math.round(collegeScore))),
        });
      });

      console.log("Final resultScores:", resultScores);
      setAreaScores(resultScores);
    }

    return resultScores;
  };

  const fetchRunComparisons = async () => {
    let attemptsQuery = supabase
      .from("attempts")
      .select("run_id, overall_score, completed, user_id");

    if (selectedRun !== "all") {
      attemptsQuery = attemptsQuery.eq("run_id", selectedRun);
    }

    const { data: attempts } = await attemptsQuery;

    if (!attempts || attempts.length === 0) {
      setRunComparisons([]);
      return;
    }

    let filteredAttempts = attempts;

    // Filter by college if selected
    if (selectedCollege !== "all") {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id")
        .eq("college_id", selectedCollege);

      const validUserIds = new Set(profiles?.map(p => p.id));
      filteredAttempts = filteredAttempts.filter(a => validUserIds.has(a.user_id));
    }

    // Filter by role if selected
    if (selectedRole !== "all") {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id")
        .eq("academic_role", selectedRole);

      const validUserIds = new Set(profiles?.map(p => p.id));
      filteredAttempts = filteredAttempts.filter(a => validUserIds.has(a.user_id));
    }

    if (filteredAttempts.length === 0) {
      setRunComparisons([]);
      return;
    }

    const runMap = new Map<
      string,
      { scores: number[]; participants: number }
    >();

    filteredAttempts
      .filter((a) => a.completed && a.overall_score)
      .forEach((attempt) => {
        const current = runMap.get(attempt.run_id) || {
          scores: [],
          participants: 0,
        };
        current.scores.push(attempt.overall_score);
        current.participants += 1;
        runMap.set(attempt.run_id, current);
      });

    const comparisons: RunComparison[] = [];

    for (const [runId, { scores, participants }] of runMap.entries()) {
      const run = runs.find((r) => r.id === runId);
      if (run) {
        comparisons.push({
          run: run.name,
          avgScore: Math.round(
            scores.reduce((sum, s) => sum + s, 0) / scores.length
          ),
          participants,
        });
      }
    }

    setRunComparisons(comparisons);
  };

  const fetchTrendData = async () => {
    let query = supabase
      .from("attempts")
      .select("completed_at, overall_score, completed, user_id")
      .eq("completed", true)
      .not("overall_score", "is", null)
      .order("completed_at", { ascending: true });

    if (selectedRun !== "all") {
      query = query.eq("run_id", selectedRun);
    }

    const { data: attempts } = await query;

    if (!attempts || attempts.length === 0) {
      setTrendData([]);
      return;
    }

    let filteredAttempts = attempts;

    // Filter by college if selected
    if (selectedCollege !== "all") {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id")
        .eq("college_id", selectedCollege);

      const validUserIds = new Set(profiles?.map(p => p.id));
      filteredAttempts = filteredAttempts.filter(a => validUserIds.has(a.user_id));
    }

    // Filter by role if selected
    if (selectedRole !== "all") {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id")
        .eq("academic_role", selectedRole);

      const validUserIds = new Set(profiles?.map(p => p.id));
      filteredAttempts = filteredAttempts.filter(a => validUserIds.has(a.user_id));
    }

    if (filteredAttempts.length === 0) {
      setTrendData([]);
      return;
    }

    const dateMap = new Map<string, { scores: number[]; count: number }>();

    filteredAttempts.forEach((attempt) => {
      if (attempt.completed_at) {
        const date = new Date(attempt.completed_at)
          .toISOString()
          .split("T")[0];
        const current = dateMap.get(date) || { scores: [], count: 0 };
        current.scores.push(attempt.overall_score);
        current.count += 1;
        dateMap.set(date, current);
      }
    });

    const trends: TrendData[] = Array.from(dateMap.entries())
      .map(([date, { scores, count }]) => ({
        date,
        avgScore: Math.round(
          scores.reduce((sum, s) => sum + s, 0) / scores.length
        ),
        participants: count,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    setTrendData(trends);
  };

  const exportToExcel = async () => {
    try {
      // Prepare workbook
      const wb = XLSX.utils.book_new();

      // Sheet 1: Summary Statistics
      const statsData = [
        ['Metric', 'Value'],
        ['Total Participants', stats.totalParticipants],
        ['Average Overall Score', `${stats.avgOverallScore}%`],
        ['Completion Rate', `${stats.completionRate}%`],
        ['Top Performing Area', stats.topArea],
        ['Weakest Area', stats.weakestArea],
        [''],
        ['Filters Applied:'],
        ['Assessment Run', selectedRun === 'all' ? 'All Runs' : runs.find(r => r.id === selectedRun)?.name || 'N/A'],
        ['College', selectedCollege === 'all' ? 'All Colleges' : colleges.find(c => c.id === selectedCollege)?.name || 'N/A'],
        ['Role', selectedRole === 'all' ? 'All Roles' : selectedRole],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(statsData);
      XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

      // Sheet 2: Competence Area Scores
      if (areaScores.length > 0) {
        const areaData = [
          ['Competence Area', 'Score (%)', 'Number of Responses'],
          ...areaScores.map(area => [area.area, area.score, area.count])
        ];
        const ws2 = XLSX.utils.aoa_to_sheet(areaData);
        XLSX.utils.book_append_sheet(wb, ws2, 'Competence Areas');
      }

      // Sheet 3: Detailed Competency Breakdown by Question
      // Fetch detailed responses for each question
      let attemptsQuery = supabase.from("attempts").select("id, user_id");
      if (selectedRun !== "all") {
        attemptsQuery = attemptsQuery.eq("run_id", selectedRun);
      }
      const { data: attempts } = await attemptsQuery;

      if (attempts && attempts.length > 0) {
        let filteredAttempts = attempts;

        // Apply college filter
        if (selectedCollege !== "all") {
          const { data: profiles } = await supabase
            .from("profiles")
            .select("id")
            .eq("college_id", selectedCollege);
          const validUserIds = new Set(profiles?.map(p => p.id));
          filteredAttempts = filteredAttempts.filter(a => validUserIds.has(a.user_id));
        }

        // Apply role filter
        if (selectedRole !== "all") {
          const { data: profiles } = await supabase
            .from("profiles")
            .select("id")
            .eq("academic_role", selectedRole);
          const validUserIds = new Set(profiles?.map(p => p.id));
          filteredAttempts = filteredAttempts.filter(a => validUserIds.has(a.user_id));
        }

        const attemptIds = filteredAttempts.map(a => a.id);

        if (attemptIds.length > 0) {
          const { data: responses } = await supabase
            .from("responses")
            .select("competence_area, question_id, rating")
            .in("attempt_id", attemptIds);

          if (responses && responses.length > 0) {
            // Group by competence area and question
            const competencyMap = new Map<string, Map<string, { total: number; count: number }>>();

            responses.forEach(response => {
              if (!competencyMap.has(response.competence_area)) {
                competencyMap.set(response.competence_area, new Map());
              }
              const areaMap = competencyMap.get(response.competence_area)!;
              const questionKey = `Q${response.question_id}`;

              const current = areaMap.get(questionKey) || { total: 0, count: 0 };
              areaMap.set(questionKey, {
                total: current.total + response.rating,
                count: current.count + 1
              });
            });

            // Build detailed data array
            const detailedData = [
              ['Competence Area', 'Question/Competency', 'Average Score', 'Response Count']
            ];

            // Sort by the 5 main areas
            const areas = [
              "Information & Data Literacy",
              "Communication & Collaboration",
              "Digital Content Creation",
              "Safety",
              "Problem Solving"
            ];

            areas.forEach(area => {
              if (competencyMap.has(area)) {
                const areaMap = competencyMap.get(area)!;
                const questions = Array.from(areaMap.entries()).sort((a, b) => {
                  const numA = parseInt(a[0].replace('Q', ''));
                  const numB = parseInt(b[0].replace('Q', ''));
                  return numA - numB;
                });

                questions.forEach(([questionKey, { total, count }]) => {
                  const avgScore = (total / count).toFixed(2);
                  detailedData.push([area, questionKey, avgScore, count]);
                });

                // Add area average
                const areaTotal = questions.reduce((sum, [_, { total }]) => sum + total, 0);
                const areaCount = questions.reduce((sum, [_, { count }]) => sum + count, 0);
                const areaAvg = (areaTotal / areaCount).toFixed(2);
                detailedData.push([`${area} - AVERAGE`, '', areaAvg, areaCount]);
                detailedData.push(['', '', '', '']); // Empty row for spacing
              }
            });

            const ws3 = XLSX.utils.aoa_to_sheet(detailedData);
            XLSX.utils.book_append_sheet(wb, ws3, 'Detailed Competencies');
          }
        }
      }

      // Sheet 4: Run Comparisons
      if (runComparisons.length > 0) {
        const runData = [
          ['Assessment Run', 'Average Score (%)', 'Participants'],
          ...runComparisons.map(run => [run.run, run.avgScore, run.participants])
        ];
        const ws4 = XLSX.utils.aoa_to_sheet(runData);
        XLSX.utils.book_append_sheet(wb, ws4, 'Run Comparisons');
      }

      // Sheet 5: Trend Over Time
      if (trendData.length > 0) {
        const trendDataSheet = [
          ['Date', 'Average Score (%)', 'Participants'],
          ...trendData.map(trend => [trend.date, trend.avgScore, trend.participants])
        ];
        const ws5 = XLSX.utils.aoa_to_sheet(trendDataSheet);
        XLSX.utils.book_append_sheet(wb, ws5, 'Trend Data');
      }

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const runName = selectedRun === 'all' ? 'All' : runs.find(r => r.id === selectedRun)?.name.replace(/\s+/g, '_') || 'Selected';
      const filename = `DigComp_Analytics_${runName}_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      toast({
        title: "Success",
        description: "Analytics data exported successfully!",
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Error",
        description: "Failed to export data to Excel",
        variant: "destructive",
      });
    }
  };

  const fetchStats = async (scores: AreaScore[]) => {
    let attemptsQuery = supabase.from("attempts").select("*");

    if (selectedRun !== "all") {
      attemptsQuery = attemptsQuery.eq("run_id", selectedRun);
    }

    const { data: attempts } = await attemptsQuery;

    if (!attempts) {
      return;
    }

    let filteredAttempts = attempts;

    // Filter by college if selected
    if (selectedCollege !== "all") {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id")
        .eq("college_id", selectedCollege);

      const validUserIds = new Set(profiles?.map(p => p.id));
      filteredAttempts = filteredAttempts.filter(a => validUserIds.has(a.user_id));
    }

    // Filter by role if selected
    if (selectedRole !== "all") {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id")
        .eq("academic_role", selectedRole);

      const validUserIds = new Set(profiles?.map(p => p.id));
      filteredAttempts = filteredAttempts.filter(a => validUserIds.has(a.user_id));
    }

    const completed = filteredAttempts.filter((a) => a.completed);
    const totalParticipants = new Set(filteredAttempts.map((a) => a.user_id)).size;
    const avgOverallScore =
      completed.length > 0
        ? Math.round(
          completed.reduce((sum, a) => sum + (a.overall_score || 0), 0) /
          completed.length
        )
        : 0;
    const completionRate =
      filteredAttempts.length > 0
        ? Math.round((completed.length / filteredAttempts.length) * 100)
        : 0;

    let topArea = "";
    let weakestArea = "";
    if (scores.length > 0) {
      const sortedAreas = [...scores].sort((a, b) => b.score - a.score);
      topArea = sortedAreas[0].area;
      weakestArea = sortedAreas[sortedAreas.length - 1].area;
    }

    setStats({
      totalParticipants,
      avgOverallScore,
      completionRate,
      topArea,
      weakestArea,
    });
  };

  if (isLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) return null;

  const radarData = areaScores.map((area) => ({
    area: area.area,
    score: area.score,
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Bilingual Instructions */}
        <Card className="p-8 mb-6 bg-gradient-to-r from-primary/5 to-accent/5 border-primary/30 shadow-lg">
          <div className="space-y-3">
            <p className="text-lg md:text-xl font-semibold text-foreground leading-relaxed">
              <strong className="text-primary text-xl md:text-2xl">Instructions:</strong> Analyze assessment data with detailed charts showing trends over time, comparisons across assessment runs, and competence area insights.
            </p>
            <p className="text-lg md:text-xl font-semibold text-foreground font-tajawal leading-relaxed" dir="rtl">
              <strong className="text-primary text-xl md:text-2xl">التعليمات:</strong> تحليل بيانات التقييم باستخدام الرسوم البيانية التفصيلية التي تظهر الاتجاهات مع مرور الوقت، والمقارنات عبر جلسات التقييم، ورؤى مجالات الكفاءة.
            </p>
          </div>
        </Card>

        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-foreground">
            Analytics Dashboard
          </h1>
          <div className="flex gap-3">
            <Button
              onClick={exportToExcel}
              className="gap-2"
            >
              <FileDown className="h-4 w-4" />
              Export to Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin")}
              className="gap-2"
            >
              Admin Panel
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="gap-2"
            >
              <Home className="h-4 w-4" />
              Home
            </Button>
          </div>
        </div>

        {/* Filter */}
        <Card className="p-6 mb-8">
          <div className="flex items-center gap-4">
            <Label htmlFor="run-filter" className="text-foreground font-semibold">
              Filter by Assessment Run:
            </Label>
            <select
              id="run-filter"
              className="h-10 rounded-md border border-input bg-background px-3 py-2 min-w-[200px]"
              value={selectedRun}
              onChange={(e) => setSelectedRun(e.target.value)}
            >
              <option value="all">All Runs</option>
              {runs.map((run) => (
                <option key={run.id} value={run.id}>
                  {run.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-4">
            <Label htmlFor="college-filter" className="text-foreground font-semibold">
              Filter by College:
            </Label>
            <select
              id="college-filter"
              className="h-10 rounded-md border border-input bg-background px-3 py-2 min-w-[200px]"
              value={selectedCollege}
              onChange={(e) => setSelectedCollege(e.target.value)}
            >
              <option value="all">All Colleges</option>
              {colleges.map((college) => (
                <option key={college.id} value={college.id}>
                  {college.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-4">
            <Label htmlFor="role-filter" className="text-foreground font-semibold">
              Filter by Role:
            </Label>
            <select
              id="role-filter"
              className="h-10 rounded-md border border-input bg-background px-3 py-2 min-w-[200px]"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="student">Students</option>
              <option value="faculty">Faculty</option>
            </select>
          </div>
        </Card>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/20 rounded-lg">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Participants</p>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalParticipants}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-success/20 rounded-lg">
                <Award className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Score</p>
                <p className="text-2xl font-bold text-foreground">
                  {stats.avgOverallScore}%
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-accent/20 rounded-lg">
                <Target className="h-6 w-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completion</p>
                <p className="text-2xl font-bold text-foreground">
                  {stats.completionRate}%
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-success/20 rounded-lg">
                <TrendingUp className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Top Area</p>
                <p className="text-sm font-bold text-foreground">
                  {stats.topArea || "N/A"}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-destructive/20 rounded-lg">
                <TrendingUp className="h-6 w-6 text-destructive rotate-180" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Weakest Area</p>
                <p className="text-sm font-bold text-foreground">
                  {stats.weakestArea || "N/A"}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Competence Area Radar */}
          <Card className="p-6">
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Competence Areas Overview
            </h2>
            {radarData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis
                    dataKey="area"
                    tick={{ fill: "hsl(var(--foreground))", fontSize: 11 }}
                  />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="hsl(var(--primary))"
                    fill="hsl(var(--primary))"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground py-12">
                No data available
              </p>
            )}
          </Card>

          {/* Area Scores Bar Chart */}
          <Card className="p-6">
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Competence Area Performance
            </h2>
            {areaScores.length > 0 ? (
              <>
                {console.log("Rendering chart with areaScores:", areaScores)}
                <ResponsiveContainer width="100%" height={300}>
                <BarChart data={areaScores} margin={{ top: 20, right: 30, left: 0, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="area"
                    tick={{ fill: "hsl(var(--foreground))", fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis domain={[0, 100]} tick={{ fill: "hsl(var(--foreground))" }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="score" fill="#3b82f6" name="My Score %" barSize={50} />
                  <Bar dataKey="globalAvg" fill="#10b981" name="Global Avg %" barSize={50} />
                  <Bar dataKey="collegeAvg" fill="#f59e0b" name="College Avg %" barSize={50} />
                </BarChart>
              </ResponsiveContainer>
              </>
            ) : (
              <p className="text-center text-muted-foreground py-12">
                No data available
              </p>
            )}
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Run Comparison */}
          <Card className="p-6">
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Assessment Run Comparison
            </h2>
            {runComparisons.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={runComparisons}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="run" tick={{ fill: "hsl(var(--foreground))" }} />
                  <YAxis domain={[0, 100]} tick={{ fill: "hsl(var(--foreground))" }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="avgScore" fill="hsl(var(--primary))" name="Avg Score" />
                  <Bar
                    dataKey="participants"
                    fill="hsl(var(--accent))"
                    name="Participants"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground py-12">
                No data available
              </p>
            )}
          </Card>

          {/* Trend Over Time */}
          <Card className="p-6">
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              Performance Trend Over Time
            </h2>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "hsl(var(--foreground))", fontSize: 10 }}
                  />
                  <YAxis domain={[0, 100]} tick={{ fill: "hsl(var(--foreground))" }} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="avgScore"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    name="Avg Score"
                  />
                  <Line
                    type="monotone"
                    dataKey="participants"
                    stroke="hsl(var(--accent))"
                    strokeWidth={2}
                    name="Participants"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground py-12">
                No data available
              </p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
